// What we send instead of the raw mic: mic -> RNNoise -> voice gate -> push-to-talk -> one MediaStreamTrack.
//
// The browser's noiseSuppression handles steady noise (fans, hum) but lets
// transients like keyboard clicks through. RNNoise (vendored, see
// /vendor/web-noise-suppressor/0.4.1/) takes out most non-speech, and the gate
// (denoise-gate.js) only opens on sustained speech-band energy, which a click
// train never has.
//
//   mic ─ source ─┬─ [rnnoise] ─ gate ─ wet ─┬─ out ─ talk ─┬─ destination  (published track)
//                 └──────────────────── dry ─┘              └─ analyser     (level meter)
//
// "降噪" off crossfades wet -> dry; push-to-talk drives `talk` (0 unless held).
// "键盘声抑制" (on by default, only acts with 降噪 on) is in the gate: it mutes key sounds the gate detects as
// impulses, and every keydown/keyup in this page is posted to the gate so it treats the next ~220 ms as typing.
// "人声隔离" (the other kind of 降噪; "标准降噪" is the only one offered for now, see denoiseKind) also takes out other people's voices in the room (a TV,
// someone across the room): the gate's thresholds follow the user's own voice level (denoise-gate.js, near), which
// the gate learns and the page keeps per physical mic (deck.voiceRef.<groupId or deviceId>); 校准 measures it from one sentence. It needs the
// browser's automatic gain control off (micConstraints), so switching kinds takes the mic again (app.js remic).
// Same track throughout, so nothing is renegotiated. Mute stays on the mic track
// (track.enabled = false): the source then emits silence, so mute overrides
// everything here. The UI for these settings is voice-settings.js.
const V = '/vendor/web-noise-suppressor/0.4.1/';

const pref = (key, dflt) => { try { return localStorage.getItem(key) ?? dflt; } catch { return dflt; } };
const setPref = (key, v) => { try { localStorage.setItem(key, v); } catch {} };
/** "降噪" on unless turned off. */
export const denoiseWanted = () => pref('deck.denoise', 'on') !== 'off';
export const rememberDenoise = (on) => setPref('deck.denoise', on ? 'on' : 'off');
/** Agora's strength is independent of the SFU denoiser; unknown/old values keep the gentler default. */
let temporaryAgoraLevel = null;
export const agoraDenoiseLevel = () => temporaryAgoraLevel ?? (pref('deck.agoraDenoiseLevel', 'SOFT') === 'AGGRESSIVE' ? 'AGGRESSIVE' : 'SOFT');
export const rememberAgoraDenoiseLevel = (level) => {
  const next = level === 'AGGRESSIVE' ? 'AGGRESSIVE' : 'SOFT';
  try { localStorage.setItem('deck.agoraDenoiseLevel', next); temporaryAgoraLevel = null; } catch { temporaryAgoraLevel = next; }
};
/** "键盘声抑制" on unless turned off. */
export const keynoiseWanted = () => pref('deck.keynoise', 'on') !== 'off';
export const rememberKeynoise = (on) => setPref('deck.keynoise', on ? 'on' : 'off');
/** Kind of 降噪: 'std' (标准降噪) or 'iso' (人声隔离). 人声隔离 is off for everyone for now, also for whoever picked it:
 *  its gate cut the quiet ends of sentences (artin 2026-11-03). Its code stays; KINDS in voice-settings.js hides the choice. */
export const ISO_OFFERED = false;
export const denoiseKind = () => (ISO_OFFERED && pref('deck.denoiseKind', 'std') === 'iso' ? 'iso' : 'std');
export const rememberDenoiseKind = (k) => setPref('deck.denoiseKind', k === 'std' ? 'std' : 'iso');
/** 人声隔离 is what we'd run: 降噪 on, kind iso, and a browser that can run the gate (else the browser's gain control stays on). */
export const isoWanted = () => denoiseWanted() && denoiseKind() === 'iso' && typeof AudioWorkletNode !== 'undefined' && !gateBroken;
let gateBroken = false; // the gate worklet didn't load here: nothing would stand in for the browser's gain control
/** The user's voice level on a physical mic (dBFS, denoise-gate.js REF), learned or measured by 校准; null if not yet.
 *  Keyed by the mic the track really got (Chrome's "default" device has the groupId of the one it stands for). */
const micKey = (track) => { const st = track?.getSettings?.() || {}; return st.groupId || st.deviceId || micDevice() || 'default'; };
const refKey = (key) => 'deck.voiceRef.' + key;
const voiceRef = (key) => { const v = parseFloat(pref(refKey(key), '')); return Number.isFinite(v) ? v : null; };
const rememberVoiceRef = (v, key) => { if (Number.isFinite(v)) setPref(refKey(key), v.toFixed(1)); };
/**
 * getUserMedia audio constraints: the browser's own echo cancellation / noise suppression / gain control, and voice
 * isolation where the browser has it (Chrome on some platforms; an unknown constraint is just ignored, but ask only
 * where it is listed). Mono: everything after this is mono anyway. 人声隔离: no automatic gain control (with nobody
 * talking it turns a TV up to speech level; the gate does its own gain from the user's voice).
 */
export function micConstraints() {
  const a = { echoCancellation: true, noiseSuppression: true, autoGainControl: !isoWanted(), channelCount: 1 };
  const dev = micDevice(); if (dev) a.deviceId = { ideal: dev }; // ideal: gone (unplugged), the system's mic instead
  try { if (navigator.mediaDevices.getSupportedConstraints?.().voiceIsolation) a.voiceIsolation = true; } catch {}
  return { audio: a };
}
/** The mic picked on the pre-join page or in 设置 → 语音 (a deviceId), or '' for the system's default. */
export const micDevice = () => pref('deck.micDevice', '');
export const rememberMicDevice = (id) => setPref('deck.micDevice', id || '');
/** Voice mode: 'open' (开放麦克风, default) or 'ptt' (按住说话). */
export const voiceMode = () => (pref('deck.voiceMode', 'open') === 'ptt' ? 'ptt' : 'open');
export const rememberVoiceMode = (m) => setPref('deck.voiceMode', m === 'ptt' ? 'ptt' : 'open');

/** An AudioContext made after an await may start suspended (Safari, autoplay rules); a suspended one would publish
 *  silence, or (the one playing the others: app.js) play nothing. It resumes on the next tap or key: iOS Safari lets
 *  resume() start audio only inside a touchend / click (not pointerdown), so those count too. Gone once the context
 *  is closed. */
export function keepRunning(ctx) {
  const EV = ['pointerdown', 'touchend', 'click', 'keydown'];
  const kick = () => {
    if (ctx.state === 'closed') { for (const ev of EV) removeEventListener(ev, kick, { capture: true }); return; }
    if (ctx.state === 'suspended' || ctx.state === 'interrupted') ctx.resume().catch(() => {}); // interrupted: iOS, after a call or Siri
  };
  kick();
  for (const ev of EV) addEventListener(ev, kick, { capture: true, passive: true });
  ctx.onstatechange = kick; // e.g. iOS interrupting the session
}

// Only the SIMD build is vendored (every browser we target has Wasm SIMD: Chrome 91+, Firefox 89+, Safari 16.4+); without it, gate only.
const SIMD = new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11]);
async function loadRnnoise(ctx) {
  if (!WebAssembly.validate(SIMD)) throw new Error('no Wasm SIMD');
  const [wasm] = await Promise.all([
    fetch(V + 'rnnoise_simd.wasm').then((r) => { if (!r.ok) throw new Error('rnnoise_simd.wasm ' + r.status); return r.arrayBuffer(); }),
    ctx.audioWorklet.addModule(V + 'rnnoiseWorklet.js'),
  ]);
  return new AudioWorkletNode(ctx, '@sapphi-red/web-noise-suppressor/rnnoise', {
    channelCount: 1, channelCountMode: 'explicit', outputChannelCount: [1], processorOptions: { maxChannels: 1, wasmBinary: wasm },
  });
}

/**
 * Build the send pipeline for a getUserMedia stream. Without AudioWorklet there
 * is no denoise stage (dry only) but push-to-talk still works. RNNoise needs
 * 48 kHz; if the browser can't feed the mic into a 48 kHz context, gate alone.
 * opts: { on, ptt, keynoise, iso, agcAsked, rnnoise (default true), gate: {openMs, holdMs, lookaheadMs, ...} }
 * agcAsked: the autoGainControl the mic was asked for (micConstraints(); default: as micConstraints() says now). Kept
 * as asked, never read back from the track: Safari doesn't list it (app.js remic compares it with what is wanted).
 * 人声隔离 runs whether or not the browser could turn its gain control off (Safari can't be asked).
 */
export async function micPipeline(mic, opts = {}) {
  let on = opts.on ?? denoiseWanted(), keys = opts.keynoise ?? keynoiseWanted(), talking = !(opts.ptt ?? voiceMode() === 'ptt');
  const agcAsked = opts.agcAsked ?? micConstraints().audio.autoGainControl;
  let kind = opts.iso === undefined ? denoiseKind() : opts.iso ? 'iso' : 'std';
  const dev = micKey(mic.getAudioTracks()[0]), nearOn = () => on && kind === 'iso';
  let cal = null; // a 校准 in progress: its resolve
  let ctx = new AudioContext({ sampleRate: 48000, latencyHint: 'interactive' }), src;
  opts.onContext?.(ctx); // the caller owns even a context whose source or worklet setup fails
  try { src = ctx.createMediaStreamSource(mic); }
  catch { ctx.close(); ctx = new AudioContext({ latencyHint: 'interactive' }); opts.onContext?.(ctx); src = ctx.createMediaStreamSource(mic); } // Firefox: mic rate != 48 kHz
  keepRunning(ctx);
  let gate = null, rnn = null;
  try {
    await ctx.audioWorklet.addModule(new URL('./denoise-gate.js', import.meta.url).pathname);
    const near = { on: nearOn(), ...(voiceRef(dev) !== null && { ref: voiceRef(dev) }) };
    gate = new AudioWorkletNode(ctx, 'deck-voice-gate', { channelCount: 1, channelCountMode: 'explicit', outputChannelCount: [1], processorOptions: { keys, near, ...opts.gate } });
    gate.port.onmessage = (e) => {
      const d = e.data || {};
      if (d.t === 'ref') rememberVoiceRef(d.ref, dev);
      else if (d.t === 'calibrated') { if (d.ref !== null) rememberVoiceRef(d.ref, dev); cal?.(d.ref); cal = null; }
    };
    if (opts.rnnoise !== false && ctx.sampleRate === 48000) {
      try { rnn = await loadRnnoise(ctx); } catch (e) { console.warn('RNNoise unavailable, voice gate only:', e); }
    }
  } catch (e) { console.warn('AudioWorklet unavailable, no denoise:', e); if (!gate) gateBroken = true; }
  const wet = ctx.createGain(), dry = ctx.createGain(), out = ctx.createGain(), talk = ctx.createGain();
  const mix = (t) => { const w = on && gate ? 1 : 0; if (t === undefined) { wet.gain.value = w; dry.gain.value = 1 - w; } else { wet.gain.setTargetAtTime(w, t, 0.015); dry.gain.setTargetAtTime(1 - w, t, 0.015); } };
  mix(); talk.gain.value = talking ? 1 : 0;
  if (gate) (rnn ? src.connect(rnn).connect(gate) : src.connect(gate)).connect(wet).connect(out);
  src.connect(dry).connect(out); out.connect(talk);
  const dest = ctx.createMediaStreamDestination(); dest.channelCount = 1; talk.connect(dest);
  const an = ctx.createAnalyser(); an.fftSize = 512; talk.connect(an);
  const buf = new Float32Array(an.fftSize);
  // Typing in this page: tell the gate about every key press and release (not auto-repeat: a held key is silent).
  // Space in 按住说话 is the talk key, not typing: it would put the start of every turn in typing mode.
  const onKey = (e) => { if (gate && keys && !e.repeat && !(e.code === 'Space' && voiceMode() === 'ptt')) gate.port.postMessage({ t: 'key' }); };
  for (const ev of ['keydown', 'keyup']) addEventListener(ev, onKey, { capture: true, passive: true });
  return {
    track: dest.stream.getAudioTracks()[0],
    denoise: !!gate, rnnoise: !!rnn, agcAsked,
    get keynoise() { return keys; },
    /** 'iso' | 'std': the kind of 降噪 asked for; iso: 人声隔离 is running now (on, iso, a mic without gain control). */
    get kind() { return kind; },
    get iso() { return !!gate && nearOn(); },
    ctx,
    /** The last node before the published track (what 语音转文字 taps, public/stt.js): exactly what the others hear. */
    node: talk,
    get on() { return on; },
    get talking() { return talking; },
    /** RMS of what we actually send, same scale as app.js's analyser(). */
    level() { an.getFloatTimeDomainData(buf); let s = 0; for (const v of buf) s += v * v; return Math.sqrt(s / buf.length); },
    setOn(v) { on = !!v; mix(ctx.currentTime); gate?.port.postMessage({ t: 'near', on: nearOn() }); },
    /** 标准降噪 / 人声隔离. A mic taken with gain control on stays 标准 until app.js takes it again without (remic). */
    setKind(k) { kind = k === 'std' ? 'std' : 'iso'; gate?.port.postMessage({ t: 'near', on: nearOn() }); },
    /** 校准: measure the user's voice while they say a sentence (up to `ms`). Resolves to the level (dBFS), or null if
     *  there was too little voice, or nothing louder than the room was just before (the TV). */
    calibrate(ms = 8000) {
      if (!gate) return Promise.resolve(null);
      cal?.(null);
      return new Promise((res) => { cal = res; gate.port.postMessage({ t: 'calibrate', ms }); });
    },
    setKeynoise(v) { keys = !!v; gate?.port.postMessage({ t: 'keynoise', on: keys }); },
    /** Push-to-talk: send (true) or silence (false); open mic keeps it true. ~5 ms in, ~30 ms out. */
    setTalk(v, immediate = false) {
      talking = !!v;
      if (immediate) { talk.gain.cancelScheduledValues(ctx.currentTime); talk.gain.setValueAtTime(talking ? 1 : 0, ctx.currentTime); }
      else talk.gain.setTargetAtTime(talking ? 1 : 0, ctx.currentTime, talking ? 0.005 : 0.03);
    },
    close() {
      for (const ev of ['keydown', 'keyup']) removeEventListener(ev, onKey, { capture: true });
      cal?.(null); cal = null; // a 校准 under way fails rather than hangs
      rnn?.port.postMessage('destroy'); return ctx.close();
    },
  };
}

/**
 * When micPipeline() can't be built at all: the raw mic track, with the same interface. No 降噪, and push-to-talk
 * is done by switching the track itself on and off, so 按住说话 never turns into an open mic. Mute goes through
 * setMuted() here (the track is shared), and wins over talking. level: a meter for the raw mic (or () => 0).
 */
export function rawMic(mic, { level = () => 0, ptt = voiceMode() === 'ptt', muted = false, agcAsked = micConstraints().audio.autoGainControl } = {}) {
  const track = mic.getAudioTracks()[0];
  gateBroken = true; // no gate here: from now on the mic is asked with the browser's gain control (app.js remic)
  let talking = !ptt;
  const sync = () => { if (track) track.enabled = talking && !muted; };
  sync();
  return {
    track, raw: true, denoise: false, rnnoise: false, on: false, keynoise: false, kind: 'std', iso: false, agcAsked, level,
    get talking() { return talking; },
    setOn() {}, setKeynoise() {}, setKind() {}, calibrate: () => Promise.resolve(null),
    setTalk(v) { talking = !!v; sync(); },
    setMuted(v) { muted = !!v; sync(); },
    close() {},
  };
}
